import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { TranslateService, StorageService } from '@services';
import { DataHelper } from '@helpers';

const FILTER_DATE_OPTIONS = (translateService) => {
    return [{
        label: translateService.instant('words.addition-date'),
        value: '-date',
    }, {
        label: 'A-Z',
        value: 'title'
    }, {
        label: 'Z-A',
        value: '-title'
    }]
};

const SORT_OPTIONS = (translateService: TranslateService) => [
    { label: 'Más recientes', value: '-date' },
    { label: 'Alfabético (A - Z)', value: 'title' },
    { label: 'Alfabético (Z - A)', value: '-title' }
];

@Component({
    selector: 'msc-shared-search-sort-bar',
    templateUrl: './search-sort-bar.component.html',
    styleUrls: ['./search-sort-bar.component.scss']
})
export class SharedSearchSortBarComponent implements OnInit {
    @Input() mode: string;
    @Input() query: string;
    @Input() type: string;
    @Input() statusOptions: any[] = [];
    @Input() filters: any = {};
    @Output() queryChange = new EventEmitter<string>();
    @Output() filtersChange = new EventEmitter<any>();
    @Output() viewModeChange = new EventEmitter<'grid' | 'list'>();
    @Output() statusChange = new EventEmitter<string>();
    @Output() rankingChange = new EventEmitter<string>();
    @Output() savedChange = new EventEmitter<boolean>();
    @Output() mobileFiltersClick = new EventEmitter<void>();

    // Subscription filter — el padre es la fuente de verdad
    private _subscriptionFilter: string | null = null;
    @Input() set subscriptionFilter(val: string | null) { this._subscriptionFilter = val ?? null; }
    get subscriptionFilter(): string | null { return this._subscriptionFilter; }
    @Output() subscriptionFilterChange = new EventEmitter<string | null>();

    @Input() set activeRanking(val: string) {
        if (val === '') {
            this.rankingOptions = this.rankingOptions.map(o => ({ ...o, isSelected: false }));
        } else if (val) {
            // Cuando se activa ranking, desmarca saved automáticamente
            this._activeSaved = false;
            // También marca el ranking option seleccionado
            this.rankingOptions = this.rankingOptions.map(o => ({
                ...o,
                isSelected: o.value === val
            }));
        }
    }

    @Input() set activeSaved(val: boolean) {
        this._activeSaved = val;
    }
    get activeSaved(): boolean { return this._activeSaved; }
    private _activeSaved: boolean = false;

    public selectOrderOptions: any[] = [];
    public showSortDropdown: boolean = false;
    public showRankingDropdown: boolean = false;
    public viewMode: 'grid' | 'list' = 'grid';
    public orderDate: any = '-date';
    public rankingOptions: any[] = [
        { id: 0, value: 'top',     label: 'Mejores calificados', isSelected: false },
        { id: 1, value: 'lowest',  label: 'Menor calificación',  isSelected: false },
        { id: 2, value: 'unrated', label: 'Sin calificación',    isSelected: false }
    ];

    get hasActiveStatus(): boolean {
        return this.statusOptions?.some(s => s.value !== 'all' && s.isSelected) ?? false;
    }

    get hasActiveRanking(): boolean {
        return this.rankingOptions.some(o => o.isSelected);
    }

    get filteredStatusOptions(): any[] {
        return this.statusOptions?.filter(s => s.value !== 'notStarted') ?? [];
    }

    get searchPlaceholder(): string {
        if (this.type === 'content') {
            return this.translateService.instant('words.search-my-courses');
        }
        return this.translateService.instant('words.would-like-to-learn');
    }

    onStatusTabClick(value: string): void {
        if (value !== 'all') {
            if (this._subscriptionFilter !== null) {
                this.subscriptionFilterChange.emit(null);
            }
            if (this.hasActiveRanking) {
                this.clearRankingFilter();
            }
        }
        this.statusChange.emit(value);
    }

    onSubscriptionTabClick(value: string | null): void {
        const next = (value !== null && this._subscriptionFilter === value) ? null : value;
        if (next !== null) {
            if (this.hasActiveStatus) {
                this.statusChange.emit('all');
            }
            if (this.hasActiveRanking) {
                this.clearRankingFilter();
            }
        }
        this.subscriptionFilterChange.emit(next);
    }

    constructor(
        private translateService: TranslateService,
        private storageService: StorageService
    ) {}

    ngOnInit(): void {
        this.selectOrderOptions = this.getSelectOrderOptions(FILTER_DATE_OPTIONS(this.translateService));
        this.getStoredFilters();
    }

    search(query: string): void {
        this.query = query;
        this.queryChange.emit(query);
    }

    toggleSortDropdown(): void {
        this.showSortDropdown = !this.showSortDropdown;
        this.showRankingDropdown = false;
    }

    toggleRankingDropdown(): void {
        this.showRankingDropdown = !this.showRankingDropdown;
        this.showSortDropdown = false;
    }

    onSelectRanking(option: any): void {
        const isAlreadySelected = this.rankingOptions.find(o => o.id === option.id)?.isSelected;
        if (isAlreadySelected) {
            // Deselect: clear all ranking selections
            this.rankingOptions = this.rankingOptions.map(o => ({ ...o, isSelected: false }));
            this.showRankingDropdown = false;
            this.rankingChange.emit('');
            return;
        }
        this.rankingOptions = this.rankingOptions.map(o => ({ ...o, isSelected: o.id === option.id }));
        this._activeSaved = false;
        // Limpiar filtros de inscripción y progreso (exclusión mutua)
        if (this._subscriptionFilter !== null) {
            this.subscriptionFilterChange.emit(null);
        }
        if (this.hasActiveStatus) {
            this.statusChange.emit('all');
        }
        this.showRankingDropdown = false;
        this.rankingChange.emit(option.value);
    }

    private clearRankingFilter(): void {
        this.rankingOptions = this.rankingOptions.map(o => ({ ...o, isSelected: false }));
        this.rankingChange.emit('');
    }

    setViewMode(mode: 'grid' | 'list'): void {
        this.viewMode = mode;
        this.viewModeChange.emit(mode);
    }

    get selectedOrderLabel(): string {
        return this.selectOrderOptions.find(o => o.isSelected)?.label || '';
    }

    getCurrentSortLabel(): string {
        return this.selectOrderOptions.find(o => o.isSelected)?.label || 'Más recientes';
    }

    getOrderDate(): string {
        switch (this.orderDate) {
            case '-date':
                return this.translateService.instant('words.addition-date');
            case 'title':
                return 'A-Z';
            case '-title':
                return 'Z-A';
        }
    }

    getSelectOrderOptions(data) {
        return data.map((d, index) => {
            const id = index;
            const label = d.label;
            const value = d.value;
            const isSelected = d.label === this.getOrderDate();
            return { id, label, value, isSelected };
        });
    }

    selectOrderDate(order: string): void {
        this.orderDate = order;
        this.filters = DataHelper.merge(this.filters, { order: this.orderDate });
        this.filtersChange.emit(this.filters);
        this.storeFilters();
    }

    onSelectOption(option) {
        this.selectOrderOptions = this.selectOrderOptions.map((selectDatum) => {
            selectDatum.isSelected = selectDatum.id === option.id;
            return selectDatum;
        });
        this.showSortDropdown = false;
        this.selectOrderDate(option.value);
    }

    private getStoredFilters(): void {
        const filters = this.storageService.getAllCache().filter((record: any) => {
            return record.key.match(this.type + '.filter.');
        });

        for (const filter of filters) {
            switch (filter.key) {
                case this.type + '.filter.order':
                    this.orderDate = filter.data;
                    this.filters = DataHelper.merge(this.filters, { order: this.orderDate });
                    this.selectOrderOptions = this.getSelectOrderOptions(FILTER_DATE_OPTIONS(this.translateService));
                    break;
                default:
                    this.filters = DataHelper.merge(this.filters, { [filter.key.replace(this.type + '.filter.', '')]: filter.data });
            }
        }

        if (filters?.length > 0) {
            this.filtersChange.emit(this.filters);
        }
    }

    private storeFilters(): void {
        this.removeStoredFilters();
        for (const key in this.filters) {
            if (this.filters.hasOwnProperty(key)) {
                if (!DataHelper.isEmpty(this.filters[key])) {
                    this.storageService.setCache(this.type + '.filter.' + key, this.filters[key], 10);
                }
            }
        }
    }

    private removeStoredFilters(): void {
        const filters = this.storageService.getAllCache().filter((record: any) => {
            return record.key.match(this.type + '.filter.');
        });

        for (const filter of filters) {
            this.storageService.deleteCache(filter.key);
        }
    }

    toggleSaved(): void {
        this._activeSaved = !this._activeSaved;
        if (this._activeSaved) {
            this.clearRankingFilter();
        }
        this.savedChange.emit(this._activeSaved);
    }
}
